import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
} from "@mui/material";
import * as datetime from "common/utils/datetime";
import { withStyles } from "@mui/styles";
import { withRouter } from "react-router";
import { withUrlResource, withUrlResourceTimezone } from "common/utils";
import { compose } from "redux";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DuplicateIcon from "@mui/icons-material/ControlPointDuplicate";
import DebouncedTextField from "common/components/DebouncedTextField";
import Icon from "@mdi/react";
import { mdiSort, mdiSortAscending, mdiSortDescending } from "@mdi/js";
import moment from "moment";
import { useTranslation } from "react-i18next";

const NotificationList = ({
  notifications,
  classes,
  history,
  urlResource,
  removeOne,
}) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState({
    field: "available_at",
    direction: "desc",
  });
  const [search, setSearch] = useState("");

  const handleSort = (field) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" },
    );
  };

  const SortIcon = ({ field }) => {
    if (sort.field !== field) {
      return (
        <Icon
          className={classes.sortIcon}
          path={mdiSort}
          size="16px"
          color="#fff"
        />
      );
    }
    return sort.direction === "asc" ? (
      <Icon
        className={classes.sortIcon}
        path={mdiSortAscending}
        size="16px"
        color="#fff"
      />
    ) : (
      <Icon
        className={classes.sortIcon}
        path={mdiSortDescending}
        size="16px"
        color="#fff"
      />
    );
  };

  const sortedNotifications = [...notifications].sort((a, b) => {
    const multiplier = sort.direction === "asc" ? 1 : -1;

    if (sort.field === "available_at" || sort.field === "available_until") {
      const aTime = moment(a[sort.field]).valueOf();
      const bTime = moment(b[sort.field]).valueOf();
      return (aTime - bTime) * multiplier;
    }

    const aVal = (a[sort.field] || "").replace(/\s+/g, "").toLowerCase();
    const bVal = (b[sort.field] || "").replace(/\s+/g, "").toLowerCase();
    if (aVal < bVal) return -1 * multiplier;
    if (aVal > bVal) return 1 * multiplier;
    return 0;
  });

  const filteredNotifications = sortedNotifications.filter((notification) =>
    (notification.title || "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Table classes={{ root: classes.root }}>
      <TableHead>
        <TableRow>
          <TableCell
            classes={{ root: classes.th }}
            className={classes.sortable}
            onClick={() => handleSort("title")}
          >
            <div className={classes.titleHeaderWrap}>
              <div className={classes.titleSortWrap}>
                {t("updates_list.th_title")}
                <SortIcon field="title" />
              </div>
              <div
                className={classes.titleSearchWrap}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <DebouncedTextField
                  id="notification-title-search"
                  placeholder={t("updates_list.th_title_search")}
                  onChange={(value) => setSearch(value || "")}
                  delay={500}
                  slotProps={{
                    htmlInput: {
                      style: {
                        color: "#fff",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        height: "32px",
                        padding: "0 8px",
                        boxSizing: "border-box",
                      },
                    },
                  }}
                  style={{ width: "270px" }}
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      color: "#fff",
                      opacity: 1,
                    },
                    "& .MuiInputBase-input:focus::placeholder": {
                      opacity: 0,
                    },
                  }}
                />
              </div>
            </div>
          </TableCell>
          <TableCell
            classes={{ root: classes.th }}
            className={classes.sortable}
            onClick={() => handleSort("n_type")}
          >
            {t("updates_list.th_type") || "Type"}
            <SortIcon field="n_type" />
          </TableCell>
          <TableCell classes={{ root: classes.thCenter }}>
            {t("updates_list.th_push")}
          </TableCell>
          <TableCell classes={{ root: classes.th }}>
            {t("updates_list.th_target")}
          </TableCell>
          <TableCell classes={{ root: classes.th }}>
            {t("updates_list.th_published")}
          </TableCell>
          <TableCell
            classes={{ root: classes.th }}
            className={classes.sortable}
            onClick={() => handleSort("available_at")}
          >
            {t("updates_list.th_available_on")}
            <SortIcon field="available_at" />
          </TableCell>
          <TableCell
            classes={{ root: classes.th }}
            className={classes.sortable}
            onClick={() => handleSort("available_until")}
          >
            {t("updates_list.th_available_until")}
            <SortIcon field="available_until" />
          </TableCell>
          <TableCell classes={{ root: classes.thCenter }}>
            {t("updates_list.th_duplicate")}
          </TableCell>
          <TableCell classes={{ root: classes.thCenter }}>
            {t("updates_list.th_delete")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {filteredNotifications.length ? (
          filteredNotifications.map((notification) => (
            <TableRow key={notification.id}>
              <TableCell
                className={classes.clickable}
                onClick={() => {
                  history.push(
                    `/${urlResource.singular}/${urlResource.id}/notifications/edit/${notification.id}`,
                  );
                }}
              >
                {notification.title}
              </TableCell>
              <TableCell className={classes.td}>
                {notification.n_type || ""}
              </TableCell>
              <TableCell className={classes.tdCenter}>
                {notification.send_push_notification ? "Yes" : "No"}
              </TableCell>
              <TableCell className={classes.td}>
                {notification.target}
              </TableCell>
              <TableCell className={classes.td}>
                {notification.published_at
                  ? datetime.full(
                      notification.published_at,
                      urlResource.timezone,
                    )
                  : ""}
              </TableCell>
              <TableCell className={classes.td}>
                {datetime.full(notification.available_at, urlResource.timezone)}
              </TableCell>
              <TableCell className={classes.td}>
                {datetime.full(
                  notification.available_until,
                  urlResource.timezone,
                )}
              </TableCell>
              <TableCell className={classes.tdCenter}>
                <IconButton color="primary">
                  <DuplicateIcon
                    onClick={() => {
                      history.push(
                        `/${urlResource.singular}/${urlResource.id}/notifications/duplicate/${notification.id}`,
                      );
                    }}
                  />
                </IconButton>
              </TableCell>
              <TableCell className={classes.tdCenter}>
                {moment(notification.available_at) > moment() && (
                  <IconButton>
                    <DeleteIcon onClick={() => removeOne(notification.id)} />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={9}>{t("updates_list.no_records")}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

NotificationList.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      send_push_notification: PropTypes.bool,
      target: PropTypes.string.isRequired,
      available_at: PropTypes.string.isRequired,
      available_until: PropTypes.string.isRequired,
    }),
  ).isRequired,
  classes: PropTypes.object.isRequired,
  removeOne: PropTypes.func.isRequired,
};

const styles = (theme) => ({
  root: {
    padding: 0,
  },
  th: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    backgroundColor: "#7f7f7f",
    color: "#fff",
    fontWeight: "bold",
  },
  thCenter: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    backgroundColor: "#7f7f7f",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
  },
  td: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  tdCenter: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    textAlign: "center",
  },
  sortable: {
    cursor: "pointer",
    userSelect: "none",
  },
  sortIcon: {
    fontSize: "1rem",
    verticalAlign: "text-top",
    marginLeft: theme.spacing(1),
  },
  titleHeaderWrap: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "15px",
  },
  titleSortWrap: {
    whiteSpace: "nowrap",
  },
  titleSearchWrap: {
    minWidth: "170px",
  },
  clickable: {
    cursor: "pointer",
    color: "#000",
    textDecoration: "underline",
    fontWeight: "bold",
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
});

const enhanced = compose(
  withRouter,
  withStyles(styles),
  withUrlResource,
  withUrlResourceTimezone,
);

export default enhanced(NotificationList);
